/**
 * Schema reader: load + validate JSON schemas from admin/schemas/
 */

import fs from 'node:fs'
import path from 'node:path'
import {
  AUTO_FIELDS,
  FORM_SUBMISSION_FIELDS,
  isColumnType,
  isFieldType,
  isFormFieldType
} from '@core-engine/schema/constants.js'
import { flattenFields, getAllFormSchemaFields } from '@core-engine/schema/field-helpers/index.js'
import type {
  FormField,
  FormSchema,
  Schema,
  SchemaField,
  SchemaSlotLayout
} from '@core-engine/schema/types.js'
import { isRecord } from '@core-engine/utils/record.js'

// ============================================================================
// Schema Types
// ============================================================================

export interface LoadedEntitySchema {
  type: 'entity'
  schema: Schema
  filePath: string
}

export interface LoadedSingleSchema {
  type: 'single'
  schema: Schema
  filePath: string
}

export interface LoadedFormSchema {
  type: 'form'
  schema: FormSchema
  filePath: string
}

export type LoadedSchema = LoadedEntitySchema | LoadedSingleSchema | LoadedFormSchema

// ============================================================================
// Schema Loading
// ============================================================================

/**
 * Load a schema by name from the schemas directory
 * Detects schema type from the explicit top-level `type` field
 */
export function loadSchema(schemasDir: string, name: string): LoadedSchema {
  const filePaths = [
    path.join(schemasDir, `${name}.json`),
    path.join(schemasDir, 'forms', `${name}.json`)
  ].filter((filePath) => fs.existsSync(filePath))

  if (filePaths.length === 0) {
    throw new SchemaNotFoundError(name, schemasDir)
  }

  if (filePaths.length > 1) {
    throw new Error(
      `Found multiple schemas named "${name}". Keep only one file: ${filePaths.join(', ')}`
    )
  }

  const filePath = filePaths[0]!
  const content = fs.readFileSync(filePath, 'utf-8')
  const parsed = parseJson(content, filePath)

  const obj = parsed as Record<string, unknown>
  const type = obj.type

  if (type === undefined) {
    throw new Error(
      `Schema "${name}" is missing required top-level "type". Expected "entity", "single", or "form".`
    )
  }

  if (type === 'form') {
    return { type: 'form', schema: parsed as FormSchema, filePath }
  }

  if (type === 'single') {
    return { type: 'single', schema: normalizeLoadedSchema(parsed), filePath }
  }

  if (type !== undefined && type !== 'entity') {
    throw new Error(
      `Schema "${name}" has unsupported type "${String(type)}". Expected "entity", "single", or "form".`
    )
  }

  return { type: 'entity', schema: normalizeLoadedEntitySchema(parsed), filePath }
}

/**
 * List all available schema names in the schemas directory
 */
export function listSchemaNames(schemasDir: string): string[] {
  if (!fs.existsSync(schemasDir)) return []

  const names = new Set<string>()
  const rootFiles = fs
    .readdirSync(schemasDir)
    .filter((f) => f.endsWith('.json') && f !== 'schema.json')
    .map((f) => f.replace('.json', ''))

  for (const name of rootFiles) {
    names.add(name)
  }

  const formsDir = path.join(schemasDir, 'forms')
  if (fs.existsSync(formsDir)) {
    for (const name of fs
      .readdirSync(formsDir)
      .filter((f) => f.endsWith('.json'))
      .map((f) => f.replace('.json', ''))) {
      names.add(name)
    }
  }

  return Array.from(names).sort()
}

// ============================================================================
// Schema Validation
// ============================================================================

const RESERVED_AUTHORSHIP_FIELD_NAMES = new Set([
  'createdBy',
  'updatedBy',
  'createdByUser',
  'updatedByUser'
])
const FORM_COLUMN_TYPES = new Set(['text', 'date', 'email', 'number', 'badge'])
const GENERATED_SCHEMA_TITLE_FIELD_NAME = 'title'
const ENTITY_SLUG_FIELD_NAME = 'slug'
const ENTITY_SLUG_FIELD_TYPES = new Set(['string', 'varchar', 'text'])
const GENERATED_SCHEMA_TITLE_FIELD_TYPES = new Set(['string', 'varchar', 'text'])
const FORM_SCHEMA_TITLE_FIELD_TYPES = new Set(['text'])
const FORM_SUBMISSION_COLUMN_NAMES = [
  AUTO_FIELDS.ID,
  FORM_SUBMISSION_FIELDS.SUBMITTED_AT,
  FORM_SUBMISSION_FIELDS.IP_ADDRESS,
  FORM_SUBMISSION_FIELDS.USER_AGENT,
  AUTO_FIELDS.CREATED_AT,
  AUTO_FIELDS.UPDATED_AT
] as const

function isEntitySlugField(field: SchemaField): boolean {
  return field.name === ENTITY_SLUG_FIELD_NAME
}

function isStringLikeSlugField(field: SchemaField): boolean {
  return ENTITY_SLUG_FIELD_TYPES.has(field.type)
}

function isGeneratedSchemaTitleField(field: SchemaField): boolean {
  return (
    field.name === GENERATED_SCHEMA_TITLE_FIELD_NAME &&
    GENERATED_SCHEMA_TITLE_FIELD_TYPES.has(field.type)
  )
}

function hasGeneratedSchemaTitleField(fields: SchemaField[]): boolean {
  return flattenFields(fields).some(isGeneratedSchemaTitleField)
}

function hasFormSchemaTitleField(schema: FormSchema): boolean {
  return getAllFormSchemaFields(schema).some(
    (field) =>
      field.name === GENERATED_SCHEMA_TITLE_FIELD_NAME &&
      FORM_SCHEMA_TITLE_FIELD_TYPES.has(field.type)
  )
}

function collectMissingGeneratedSchemaTitleErrors(
  schema: Schema,
  schemaKind: 'Entity' | 'Single',
  errors: string[]
): void {
  if (hasGeneratedSchemaTitleField(schema.fields || [])) return
  errors.push(
    `${schemaKind} schema "${schema.name}" must define a text-compatible field named "title".`
  )
}

function collectMissingFormSchemaTitleErrors(schema: FormSchema, errors: string[]): void {
  if (hasFormSchemaTitleField(schema)) return
  errors.push(`Form schema "${schema.name}" must define a text-compatible field named "title".`)
}

function hasPersistedTitleField(fields: SchemaField[]): boolean {
  return flattenFields(fields).some(
    (field) => field.name === GENERATED_SCHEMA_TITLE_FIELD_NAME && isStringLikeSlugField(field)
  )
}

function createGeneratedSlugField(fields: SchemaField[]): SchemaField {
  const sourceField = hasPersistedTitleField(fields) ? 'title' : undefined

  return {
    name: ENTITY_SLUG_FIELD_NAME,
    type: 'string',
    label: 'Slug',
    required: true,
    hint: 'URL-friendly identifier',
    ...(sourceField ? { defaultValueFrom: sourceField } : {})
  }
}

function normalizeEntitySlugField(field: SchemaField, sourceField?: string): SchemaField {
  return {
    ...field,
    required: true,
    ...(sourceField && !field.hidden ? { defaultValueFrom: sourceField } : {})
  }
}

function createGeneratedSlugFieldForTitle(
  fields: SchemaField[],
  titleField: SchemaField
): SchemaField {
  const slugField = createGeneratedSlugField(fields)
  return titleField.slot ? { ...slugField, slot: titleField.slot } : slugField
}

function orderEntityIdentityFields(fields: SchemaField[]): SchemaField[] {
  const titleIndex = fields.findIndex(isGeneratedSchemaTitleField)
  const slugIndex = fields.findIndex(isEntitySlugField)

  if (titleIndex < 0 || slugIndex < 0 || (titleIndex === 0 && slugIndex === 1)) return fields

  const titleField = fields[titleIndex]!
  const slugField = fields[slugIndex]!

  return [
    titleField,
    slugField,
    ...fields.filter((_, index) => index !== titleIndex && index !== slugIndex)
  ]
}

interface EntityIdentityNormalizationState {
  sourceField?: string
  hasAuthoredSlugField: boolean
  keptAuthoredSlugField: boolean
  generatedSlugInserted: boolean
}

function normalizeEntityIdentityFields(
  fields: SchemaField[],
  rootFields: SchemaField[],
  state: EntityIdentityNormalizationState
): SchemaField[] {
  const normalizedFields: SchemaField[] = []

  for (const field of fields) {
    if (isEntitySlugField(field)) {
      if (state.keptAuthoredSlugField) continue
      state.keptAuthoredSlugField = true
      normalizedFields.push(normalizeEntitySlugField(field, state.sourceField))
      continue
    }

    if ((field.type === 'group' || field.type === 'section') && field.fields) {
      normalizedFields.push({
        ...field,
        fields: normalizeEntityIdentityFields(field.fields, rootFields, state)
      })
      continue
    }

    if (field.type === 'tabs' && field.tabs) {
      normalizedFields.push({
        ...field,
        tabs: field.tabs.map((tab) => ({
          ...tab,
          fields: normalizeEntityIdentityFields(tab.fields ?? [], rootFields, state)
        }))
      })
      continue
    }

    normalizedFields.push(field)

    if (
      !state.hasAuthoredSlugField &&
      !state.generatedSlugInserted &&
      isGeneratedSchemaTitleField(field)
    ) {
      normalizedFields.push(createGeneratedSlugFieldForTitle(rootFields, field))
      state.generatedSlugInserted = true
    }
  }

  return orderEntityIdentityFields(normalizedFields)
}

function ensureEntitySlugField(schema: Schema): Schema {
  const sourceField = hasPersistedTitleField(schema.fields) ? 'title' : undefined
  const hasAuthoredSlugField = flattenFields(schema.fields).some(isEntitySlugField)
  const state: EntityIdentityNormalizationState = {
    sourceField,
    hasAuthoredSlugField,
    keptAuthoredSlugField: false,
    generatedSlugInserted: false
  }
  const normalizedFields = normalizeEntityIdentityFields(schema.fields, schema.fields, state)

  if (hasAuthoredSlugField || state.generatedSlugInserted) {
    return {
      ...schema,
      fields: normalizedFields
    }
  }

  const insertIndex = Math.min(normalizedFields.length, 1)
  const fields = [
    ...normalizedFields.slice(0, insertIndex),
    createGeneratedSlugField(schema.fields),
    ...normalizedFields.slice(insertIndex)
  ]

  return {
    ...schema,
    fields
  }
}

function collectInvalidEntitySlugErrors(schema: Schema, errors: string[]): void {
  for (const field of flattenFields(schema.fields || [])) {
    if (!isEntitySlugField(field)) continue
    if (!isStringLikeSlugField(field)) {
      errors.push(
        `Entity schema "${schema.name}" declares slug as "${field.type}", but built-in Entity slugs must use "string", "varchar", or "text".`
      )
    }
  }
}

function collectReservedAuthorshipFieldErrors(
  schema: Schema,
  schemaKind: 'entity' | 'single',
  errors: string[]
): void {
  for (const field of flattenFields(schema.fields || [])) {
    if (RESERVED_AUTHORSHIP_FIELD_NAMES.has(field.name)) {
      errors.push(
        `${schemaKind} schema "${schema.name}" declares reserved field "${field.name}". BetterStart generates "${field.name}" as authorship metadata or companion data.`
      )
    }
  }
}

function collectInvalidDefaultValueFromErrors(schema: Schema, errors: string[]): void {
  const flatFields = flattenFields(schema.fields || [])
  const fieldMap = new Map(flatFields.map((field) => [field.name, field]))

  for (const field of flatFields) {
    if (field.defaultValueFrom === undefined) continue
    if (typeof field.defaultValueFrom !== 'string' || field.defaultValueFrom.trim().length === 0) {
      errors.push(
        `Field "${field.name}" has invalid defaultValueFrom="${String(field.defaultValueFrom)}". Expected the name of another field in the same schema.`
      )
      continue
    }

    const sourceField = field.defaultValueFrom.trim()
    if (field.primaryKey || field.name === 'id') {
      errors.push(
        `Field "${field.name}" cannot use defaultValueFrom because generated ID and primary key fields are owned by BetterStart.`
      )
    } else if (sourceField === field.name) {
      errors.push(`Field "${field.name}" cannot use itself as defaultValueFrom.`)
    } else {
      const source = fieldMap.get(sourceField)
      if (!source) {
        errors.push(
          `Field "${field.name}" has defaultValueFrom="${sourceField}", but no field named "${sourceField}" exists in schema "${schema.name}".`
        )
      } else if (source.primaryKey || sourceField === 'id') {
        errors.push(
          `Field "${field.name}" has defaultValueFrom="${sourceField}", but generated ID and primary key fields cannot be used as defaultValueFrom sources.`
        )
      }
    }
  }
}

function collectInvalidSchemaFieldTypeErrors(fields: SchemaField[], errors: string[]): void {
  for (const field of fields) {
    if (field.type !== undefined && (typeof field.type !== 'string' || !isFieldType(field.type))) {
      errors.push(
        `Field "${field.name || 'unnamed'}" has unsupported type "${String(field.type)}".`
      )
    }

    if (field.fields) {
      collectInvalidSchemaFieldTypeErrors(field.fields, errors)
    }
    if (field.items) {
      collectInvalidSchemaFieldTypeErrors([field.items as SchemaField], errors)
    }
    if (field.tabs) {
      for (const tab of field.tabs) {
        collectInvalidSchemaFieldTypeErrors(tab.fields || [], errors)
        collectInvalidSchemaSlotFieldTypeErrors(tab.slot, errors)
      }
    }
  }
}

function collectInvalidSchemaSlotFieldTypeErrors(slot: unknown, errors: string[]): void {
  if (!isSchemaSlotLayout(slot)) return
  collectInvalidSchemaFieldTypeErrors(getSlotAreaFields(slot.main), errors)
  collectInvalidSchemaFieldTypeErrors(getSlotAreaFields(slot.sidebar), errors)
}

function collectInvalidColumnTypeErrors(schema: Schema, errors: string[]): void {
  for (const column of schema.columns || []) {
    if (
      column.type !== undefined &&
      (typeof column.type !== 'string' || !isColumnType(column.type))
    ) {
      errors.push(
        `Column "${column.accessorKey || 'unnamed'}" has unsupported type "${String(column.type)}".`
      )
    }
  }
}

function collectInvalidFormFieldTypeErrors(fields: FormField[], errors: string[]): void {
  for (const field of fields) {
    if (
      field.type !== undefined &&
      (typeof field.type !== 'string' || !isFormFieldType(field.type))
    ) {
      errors.push(
        `Form field "${field.name || 'unnamed'}" has unsupported type "${String(field.type)}".`
      )
    }

    if (field.fields) {
      collectInvalidFormFieldTypeErrors(field.fields, errors)
    }
  }
}

function collectInvalidFormColumnTypeErrors(schema: FormSchema, errors: string[]): void {
  for (const column of schema.columns || []) {
    if (
      column.type !== undefined &&
      (typeof column.type !== 'string' || !FORM_COLUMN_TYPES.has(column.type))
    ) {
      errors.push(
        `Form column "${column.accessorKey || 'unnamed'}" has unsupported type "${String(column.type)}".`
      )
    }
  }
}

function collectInvalidFormColumnReferenceErrors(schema: FormSchema, errors: string[]): void {
  const fieldNames = new Set(
    getAllFormSchemaFields(schema)
      .map((field) => field.name)
      .filter((name): name is string => typeof name === 'string' && name.length > 0)
  )

  for (const columnName of FORM_SUBMISSION_COLUMN_NAMES) {
    fieldNames.add(columnName)
  }

  for (const column of schema.columns || []) {
    if (!column.accessorKey || !column.header) {
      errors.push(`Form column is missing required properties: ${JSON.stringify(column)}`)
    } else if (!fieldNames.has(column.accessorKey)) {
      errors.push(
        `Form column accessorKey "${column.accessorKey}" does not match any field or generated submission property. Available: ${Array.from(fieldNames).join(', ')}`
      )
    }
  }
}

/**
 * Validate an entity schema structure
 * Returns an array of error messages (empty = valid)
 */
export function validateEntitySchema(schema: Schema): string[] {
  const errors: string[] = []
  collectSchemaSlotLayoutErrors(schema, errors)
  const normalizedSchema = normalizeEntitySchema(schema)

  if (!normalizedSchema.name || typeof normalizedSchema.name !== 'string') {
    errors.push('Schema must have a valid "name" field')
  }
  if (!normalizedSchema.label || typeof normalizedSchema.label !== 'string') {
    errors.push('Schema must have a valid "label" field')
  }
  if (!normalizedSchema.description || typeof normalizedSchema.description !== 'string') {
    errors.push('Schema must have a valid "description" field')
  }
  if (!normalizedSchema.icon || typeof normalizedSchema.icon !== 'string') {
    errors.push('Schema must have a valid "icon" field')
  }
  if (!Array.isArray(normalizedSchema.fields) || normalizedSchema.fields.length === 0) {
    errors.push('Schema must have at least one field via "fields" or "slot"')
  }
  if (normalizedSchema.type !== 'entity') {
    errors.push('Entity schema must have `type: "entity"`')
  }
  if (!Array.isArray(normalizedSchema.columns) || normalizedSchema.columns.length === 0) {
    errors.push('Schema must have at least one column')
  }

  // Validate fields
  for (const field of normalizedSchema.fields || []) {
    if (field.type === 'separator') continue
    if (!field.name || !field.type) {
      errors.push(`Field is missing required properties: ${JSON.stringify(field)}`)
    }
  }

  collectInvalidSchemaFieldTypeErrors(normalizedSchema.fields || [], errors)
  collectInvalidSlotErrors(normalizedSchema.fields || [], '', errors)
  collectInvalidHeightErrors(normalizedSchema.fields || [], '', errors)
  collectMissingGeneratedSchemaTitleErrors(normalizedSchema, 'Entity', errors)
  collectInvalidDefaultValueFromErrors(normalizedSchema, errors)
  collectReservedAuthorshipFieldErrors(normalizedSchema, 'entity', errors)
  collectInvalidEntitySlugErrors(normalizedSchema, errors)
  collectInvalidColumnTypeErrors(normalizedSchema, errors)

  // Validate columns reference real fields
  const flatFields = flattenFields(normalizedSchema.fields || [])
  const fieldNames = new Set(flatFields.map((f) => f.name))
  // Auto fields are always available
  for (const auto of [
    'id',
    'createdAt',
    'updatedAt',
    'published',
    'submittedAt',
    'ipAddress',
    'userAgent',
    'createdBy',
    'updatedBy'
  ]) {
    fieldNames.add(auto)
  }

  for (const column of normalizedSchema.columns || []) {
    if (!column.accessorKey || !column.header || !column.type) {
      errors.push(`Column is missing required properties: ${JSON.stringify(column)}`)
    } else if (!fieldNames.has(column.accessorKey)) {
      errors.push(
        `Column accessorKey "${column.accessorKey}" does not match any field. Available: ${Array.from(fieldNames).join(', ')}`
      )
    }
  }

  return errors
}

/**
 * Validate a single schema structure
 * Like entity validation but skips columns requirement (no list view)
 */
export function validateSingleSchema(schema: Schema): string[] {
  const errors: string[] = []
  collectSchemaSlotLayoutErrors(schema, errors)
  const normalizedSchema = normalizeSchemaSlotLayout(schema)

  if (!normalizedSchema.name || typeof normalizedSchema.name !== 'string') {
    errors.push('Schema must have a valid "name" field')
  }
  if (!normalizedSchema.label || typeof normalizedSchema.label !== 'string') {
    errors.push('Schema must have a valid "label" field')
  }
  if (!normalizedSchema.description || typeof normalizedSchema.description !== 'string') {
    errors.push('Schema must have a valid "description" field')
  }
  if (!normalizedSchema.icon || typeof normalizedSchema.icon !== 'string') {
    errors.push('Schema must have a valid "icon" field')
  }
  if (!Array.isArray(normalizedSchema.fields) || normalizedSchema.fields.length === 0) {
    errors.push('Schema must have at least one field via "fields" or "slot"')
  }
  if (normalizedSchema.type !== 'single') {
    errors.push('Single schema must have `type: "single"`')
  }

  // Validate fields
  for (const field of normalizedSchema.fields || []) {
    if (field.type === 'separator') continue
    if (!field.name || !field.type) {
      errors.push(`Field is missing required properties: ${JSON.stringify(field)}`)
    }
  }

  collectInvalidSchemaFieldTypeErrors(normalizedSchema.fields || [], errors)
  collectInvalidSlotErrors(normalizedSchema.fields || [], '', errors)
  collectInvalidHeightErrors(normalizedSchema.fields || [], '', errors)
  collectMissingGeneratedSchemaTitleErrors(normalizedSchema, 'Single', errors)
  collectInvalidDefaultValueFromErrors(normalizedSchema, errors)
  collectReservedAuthorshipFieldErrors(normalizedSchema, 'single', errors)

  // No columns validation — single schemas have no list view

  return errors
}

/**
 * Validate a form schema structure
 */
export function validateFormSchema(schema: FormSchema): string[] {
  const errors: string[] = []

  if (schema.type !== 'form') {
    errors.push('Form schema must have `type: "form"`')
  }
  if (!schema.name || typeof schema.name !== 'string') {
    errors.push('Form schema must have a valid "name" field')
  }
  if (!schema.label || typeof schema.label !== 'string') {
    errors.push('Form schema must have a valid "label" field')
  }
  if (!schema.description || typeof schema.description !== 'string') {
    errors.push('Form schema must have a valid "description" field')
  }
  if (!schema.icon || typeof schema.icon !== 'string') {
    errors.push('Form schema must have a valid "icon" field')
  }

  const hasFields = Array.isArray(schema.fields) && schema.fields.length > 0
  const hasSteps = Array.isArray(schema.steps) && schema.steps.length > 0

  if (!hasFields && !hasSteps) {
    errors.push('Form schema must have either "fields" or "steps"')
  }
  if (hasFields && hasSteps) {
    errors.push('Form schema cannot have both "fields" and "steps"')
  }

  if (hasSteps) {
    for (const step of schema.steps!) {
      if (!step.name || !step.label) {
        errors.push(`Form step is missing name or label: ${JSON.stringify(step)}`)
      }
      if (!Array.isArray(step.fields) || step.fields.length === 0) {
        errors.push(`Form step "${step.name}" must have at least one field`)
      } else {
        collectInvalidFormFieldTypeErrors(step.fields, errors)
        collectInvalidSlotErrors(step.fields, `steps.${step.name}`, errors)
        collectInvalidHeightErrors(step.fields, `steps.${step.name}`, errors)
      }
    }
  } else if (hasFields) {
    collectInvalidFormFieldTypeErrors(schema.fields || [], errors)
    collectInvalidSlotErrors(schema.fields || [], '', errors)
    collectInvalidHeightErrors(schema.fields || [], '', errors)
  }
  collectMissingFormSchemaTitleErrors(schema, errors)
  collectInvalidFormColumnTypeErrors(schema, errors)
  collectInvalidFormColumnReferenceErrors(schema, errors)

  return errors
}

/**
 * Validate a loaded schema (auto-detects type)
 */
export function validateLoadedSchema(loaded: LoadedSchema): string[] {
  if (loaded.type === 'entity') {
    return validateEntitySchema(loaded.schema)
  }
  if (loaded.type === 'single') {
    return validateSingleSchema(loaded.schema)
  }
  return validateFormSchema(loaded.schema)
}

// ============================================================================
// Errors
// ============================================================================

export class SchemaNotFoundError extends Error {
  constructor(name: string, schemasDir: string) {
    super(
      `Schema "${name}" not found. Looked in:\n  - ${path.join(schemasDir, `${name}.json`)}\n  - ${path.join(schemasDir, 'forms', `${name}.json`)}`
    )
    this.name = 'SchemaNotFoundError'
  }
}

// ============================================================================
// Internal Helpers
// ============================================================================

function parseJson(content: string, filePath: string): unknown {
  try {
    return JSON.parse(content)
  } catch {
    throw new Error(`Failed to parse JSON schema: ${filePath}`)
  }
}

function normalizeLoadedSchema(parsed: unknown): Schema {
  const schema = parsed as Schema
  const slotLayoutErrors: string[] = []
  collectSchemaSlotLayoutErrors(schema, slotLayoutErrors)

  if (slotLayoutErrors.length > 0) {
    throw new Error(slotLayoutErrors.join('; '))
  }

  return normalizeSchemaSlotLayout(schema)
}

function normalizeLoadedEntitySchema(parsed: unknown): Schema {
  return normalizeEntitySchema(normalizeLoadedSchema(parsed))
}

export function normalizeEntitySchema(schema: Schema): Schema {
  return ensureEntitySlugField(normalizeSchemaSlotLayout(schema))
}

type SchemaWithSlotLayout = Omit<Schema, 'fields' | 'slot'> & {
  fields?: SchemaField[]
  slot?: unknown
}

type SchemaTabWithSlotLayout = {
  name: string
  label: string
  fields?: SchemaField[]
  slot?: unknown
}

export function normalizeSchemaSlotLayout(schema: Schema): Schema {
  const input = schema as SchemaWithSlotLayout
  const { slot: _slot, ...rest } = input

  return {
    ...rest,
    fields: normalizeSchemaFields(input)
  } as Schema
}

function normalizeSchemaFields(input: SchemaWithSlotLayout): SchemaField[] {
  if (isSchemaSlotLayout(input.slot)) return normalizeSlotLayoutFields(input.slot)
  return normalizeFields(input.fields ?? [])
}

function normalizeSlotLayoutFields(slot: SchemaSlotLayout): SchemaField[] {
  return [
    ...normalizeFields(getSlotAreaFields(slot.main)),
    ...normalizeFields(getSlotAreaFields(slot.sidebar), 'sidebar')
  ]
}

function normalizeFields(fields: SchemaField[], inheritedSlot?: 'sidebar'): SchemaField[] {
  return fields.map((field) => {
    const tabs = field.tabs?.map(normalizeTabSlotLayout)
    const normalizedField = tabs ? { ...field, tabs } : { ...field }
    if (inheritedSlot) return { ...normalizedField, slot: inheritedSlot }
    return normalizedField
  })
}

function normalizeTabSlotLayout(
  tab: SchemaTabWithSlotLayout
): NonNullable<SchemaField['tabs']>[number] {
  const { slot: _slot, ...rest } = tab

  if (isSchemaSlotLayout(tab.slot)) {
    return {
      ...rest,
      fields: normalizeSlotLayoutFields(tab.slot)
    }
  }

  return {
    ...rest,
    fields: normalizeFields(tab.fields ?? [])
  }
}

function isSchemaSlotLayout(value: unknown): value is SchemaSlotLayout {
  return isRecord(value)
}

function isSlotArea(value: unknown): value is { fields: SchemaField[] } {
  return isRecord(value) && Array.isArray(value.fields)
}

function getSlotAreaFields(value: unknown): SchemaField[] {
  return isSlotArea(value) ? value.fields : []
}

type SlotAwareField = {
  name?: string
  slot?: unknown
  height?: unknown
  fields?: SlotAwareField[]
  tabs?: Array<{ name?: string; fields?: SlotAwareField[]; slot?: unknown }>
}

function collectSchemaSlotLayoutErrors(schema: Schema, errors: string[]): void {
  const input = schema as SchemaWithSlotLayout
  const hasFields = input.fields !== undefined
  const hasSlot = input.slot !== undefined

  if (!hasSlot) return

  if (hasFields) {
    errors.push('Schema cannot define both top-level "fields" and "slot". Use one layout source.')
  }

  collectSlotLayoutErrors(input.slot, 'slot', errors)
}

function collectSlotLayoutErrors(value: unknown, path: string, errors: string[]): void {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object with "main" and/or "sidebar" field groups.`)
    return
  }

  const validKeys = new Set(['main', 'sidebar'])
  for (const key of Object.keys(value)) {
    if (!validKeys.has(key)) {
      errors.push(`${path} has unsupported key "${key}". Expected "main" or "sidebar".`)
    }
  }

  const areas = [
    ['main', value.main],
    ['sidebar', value.sidebar]
  ] as const
  let hasArea = false

  for (const [name, area] of areas) {
    if (area === undefined) continue
    hasArea = true
    collectSlotAreaErrors(area, `${path}.${name}`, errors)
  }

  if (!hasArea) {
    errors.push(`${path} must define at least one of "main" or "sidebar".`)
  }
}

function collectSlotAreaErrors(value: unknown, path: string, errors: string[]): void {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object with a "fields" array.`)
    return
  }

  const fields = value.fields
  if (!Array.isArray(fields)) {
    errors.push(`${path}.fields must be an array.`)
    return
  }

  for (const field of fields as SlotAwareField[]) {
    walkSlot(field, `${path}.fields.${field.name ?? 'unnamed'}`, errors)
  }
}

function collectInvalidHeightErrors(
  topLevelFields: SlotAwareField[],
  rootPath: string,
  errors: string[]
): void {
  const prefix = rootPath ? `${rootPath}.` : ''
  for (const field of topLevelFields) {
    walkHeight(field, `${prefix}${field.name ?? 'unnamed'}`, errors)
  }
}

function walkHeight(field: SlotAwareField, fieldPath: string, errors: string[]): void {
  if (field.height !== undefined && field.height !== 'auto' && field.height !== 'fill') {
    errors.push(
      `Field "${fieldPath}" has invalid height="${String(field.height)}". Expected "auto" or "fill".`
    )
  }
  if (field.fields) {
    for (const child of field.fields) {
      walkHeight(child, `${fieldPath}.${child.name ?? 'unnamed'}`, errors)
    }
  }
  if (field.tabs) {
    for (const tab of field.tabs) {
      if (tab.fields) {
        for (const child of tab.fields) {
          walkHeight(child, `${fieldPath}.${tab.name}.${child.name ?? 'unnamed'}`, errors)
        }
      }
    }
  }
}

function collectInvalidSlotErrors(
  fields: SlotAwareField[],
  rootPath: string,
  errors: string[]
): void {
  const prefix = rootPath ? `${rootPath}.` : ''
  for (const field of fields) {
    walkSlot(field, `${prefix}${field.name ?? 'unnamed'}`, errors)
  }
}

function walkSlot(field: SlotAwareField, fieldPath: string, errors: string[]): void {
  if (field.slot !== undefined && field.slot !== 'main' && field.slot !== 'sidebar') {
    errors.push(
      `Field "${fieldPath}" has invalid slot="${String(field.slot)}". Expected "main" or "sidebar".`
    )
  }
  if (field.fields) {
    for (const child of field.fields) {
      walkSlot(child, `${fieldPath}.${child.name ?? 'unnamed'}`, errors)
    }
  }
  if (field.tabs) {
    for (const tab of field.tabs) {
      const tabPath = `${fieldPath}.${tab.name ?? 'unnamed'}`
      if (tab.fields && tab.slot !== undefined) {
        errors.push(`Tab "${tabPath}" cannot define both "fields" and "slot".`)
      }
      if (tab.slot !== undefined) {
        collectSlotLayoutErrors(tab.slot, `${tabPath}.slot`, errors)
      }
      if (tab.fields) {
        for (const child of tab.fields) {
          walkSlot(child, `${tabPath}.${child.name ?? 'unnamed'}`, errors)
        }
      }
    }
  }
}
